import { useLayoutEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";

import { updateDoc } from "firebase/firestore";

import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Textarea,
  makeStyles,
} from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";

import { useMissionControlDB } from "@aglocal/web/firebase";
import useIsCurrentUser from "@/hooks/useIsCurrentUser";

const useStyles = makeStyles({
  input: {
    height: "25rem",
    width: "100%",
  },
});

export interface NotesAccordionItemProps {
  name: unknown;
  interview: Interview;
}

export default function NotesAccordionItem({
  name,
  interview,
}: NotesAccordionItemProps) {
  const { $t } = useIntl();
  const styles = useStyles();
  const db = useMissionControlDB();

  const isCurrentUser = useIsCurrentUser(interview.interviewer);

  const [notes, setNotes] = useState(interview.inMeetingNotes ?? "");

  const updateNotes = useDebouncedCallback(
    (inMeetingNotes: string) => {
      void updateDoc(db.interviews.doc(interview.id), {
        inMeetingNotes,
      });
    },
    1000,
    {
      maxWait: 5000,
    },
  );

  // Use layout effect to flush the debounced callback before it is unmounted
  useLayoutEffect(
    () => () => {
      updateNotes.flush();
    },
    [updateNotes],
  );

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(value);
    updateNotes(value);
  };

  return (
    <AccordionItem value={name}>
      <AccordionHeader>
        <FormattedMessage
          defaultMessage="Notes"
          description="Header of the in-meeting sidebar section where the recruiter writes meeting notes."
        />
      </AccordionHeader>
      <AccordionPanel>
        <Textarea
          placeholder={
            isCurrentUser
              ? $t({
                  defaultMessage: "Add meeting notes here...",
                  description:
                    "Placeholder of the notes text area when the recruiter can edit the notes.",
                })
              : $t({
                  defaultMessage: "Meeting Notes will be shown here.",
                  description:
                    "Placeholder of the notes text area when the viewer is not the meeting's recruiter and can only read the notes.",
                })
          }
          className={styles.input}
          readOnly={!isCurrentUser}
          value={notes}
          onChange={handleChange}
        />
      </AccordionPanel>
    </AccordionItem>
  );
}
